import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import api from '../../api';

const questaoVazia = () => ({ enunciado: '', explicacao: '', opcoes: [{ texto: '', correta: true }, { texto: '', correta: false }] });

export default function QuizEditor() {
  const { id } = useParams();
  const editando = !!id;
  const navigate = useNavigate();

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [notaMinima, setNotaMinima] = useState(70);
  const [questoes, setQuestoes] = useState([questaoVazia()]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!editando) return;
    api.get(`/quizzes/${id}`).then(({ data }) => {
      setTitulo(data.quiz.titulo || '');
      setDescricao(data.quiz.descricao || '');
      setNotaMinima(data.quiz.nota_minima || 70);
      setQuestoes((data.questoes || []).map(q => ({
        enunciado: q.enunciado || '', explicacao: q.explicacao || '',
        opcoes: (q.opcoes || []).map(o => ({ texto: o.texto, correta: !!o.correta })),
      })));
    }).catch(() => { Swal.fire('Erro', 'Não foi possível carregar o quiz.', 'error'); navigate('/admin/quizzes'); });
  }, [id, editando, navigate]);

  const mutQ = (i, fn) => setQuestoes(qs => qs.map((q, j) => (j === i ? fn(q) : q)));
  const addQuestao = () => setQuestoes(qs => [...qs, questaoVazia()]);
  const removeQuestao = (i) => setQuestoes(qs => qs.filter((_, j) => j !== i));
  const addOpcao = (i) => mutQ(i, q => ({ ...q, opcoes: [...q.opcoes, { texto: '', correta: false }] }));
  const removeOpcao = (i, k) => mutQ(i, q => ({ ...q, opcoes: q.opcoes.filter((_, j) => j !== k) }));
  const setOpcaoTexto = (i, k, texto) => mutQ(i, q => ({ ...q, opcoes: q.opcoes.map((o, j) => (j === k ? { ...o, texto } : o)) }));
  const marcarCorreta = (i, k) => mutQ(i, q => ({ ...q, opcoes: q.opcoes.map((o, j) => ({ ...o, correta: j === k })) }));

  async function salvar(e) {
    e.preventDefault();
    if (!titulo.trim()) return Swal.fire('Falta o título', 'Dê um nome ao quiz.', 'warning');
    for (const [i, q] of questoes.entries()) {
      if (!q.enunciado.trim()) return Swal.fire('Pergunta vazia', `A pergunta ${i + 1} está sem enunciado.`, 'warning');
      const validas = q.opcoes.filter(o => o.texto.trim());
      if (validas.length < 2) return Swal.fire('Poucas alternativas', `A pergunta ${i + 1} precisa de pelo menos 2 alternativas.`, 'warning');
      if (!q.opcoes.some(o => o.correta && o.texto.trim())) return Swal.fire('Sem resposta certa', `Marque a alternativa correta da pergunta ${i + 1}.`, 'warning');
    }
    setSalvando(true);
    const payload = {
      titulo: titulo.trim(), descricao: descricao.trim() || null, nota_minima: parseInt(notaMinima) || 70,
      questoes: questoes.map(q => ({
        enunciado: q.enunciado.trim(), explicacao: q.explicacao.trim() || null,
        opcoes: q.opcoes.filter(o => o.texto.trim()).map(o => ({ texto: o.texto.trim(), correta: !!o.correta })),
      })),
    };
    try {
      if (editando) await api.put(`/quizzes/${id}`, payload);
      else await api.post('/quizzes', payload);
      await Swal.fire({ icon: 'success', title: 'Quiz salvo!', timer: 1300, showConfirmButton: false });
      navigate(-1);
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally { setSalvando(false); }
  }

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 820 }}>
        <Link to="/admin/quizzes" className="doc-voltar"><i className="bi bi-arrow-left"></i> Quizzes</Link>
        <h1 style={{ color: 'var(--verde)', fontWeight: 700, marginBottom: 16 }}>{editando ? 'Editar quiz' : 'Novo quiz'}</h1>

        <form className="conteudo-card doc-form" onSubmit={salvar}>
          <label className="doc-label">Título *</label>
          <input className="doc-input" value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={200} autoFocus />

          <div className="doc-form-linha">
            <div style={{ flex: 2 }}>
              <label className="doc-label">Descrição</label>
              <input className="doc-input" value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Opcional" />
            </div>
            <div style={{ flex: 1 }}>
              <label className="doc-label">Nota mínima (%)</label>
              <input className="doc-input" type="number" min={1} max={100} value={notaMinima} onChange={e => setNotaMinima(e.target.value)} />
            </div>
          </div>

          <label className="doc-label" style={{ marginTop: 8 }}>Perguntas</label>
          {questoes.map((q, i) => (
            <div className="quiz-edit-q" key={i}>
              <div className="quiz-edit-q-cab">
                <span className="quiz-num">{i + 1}</span>
                <input className="doc-input" placeholder="Enunciado da pergunta" value={q.enunciado}
                       onChange={e => mutQ(i, x => ({ ...x, enunciado: e.target.value }))} />
                {questoes.length > 1 && (
                  <button type="button" className="quiz-edit-rm" onClick={() => removeQuestao(i)} title="Remover pergunta"><i className="bi bi-trash"></i></button>
                )}
              </div>
              <div className="quiz-edit-opcoes">
                {q.opcoes.map((o, k) => (
                  <div className="quiz-edit-opcao" key={k}>
                    <input type="radio" name={`correta-${i}`} checked={o.correta} onChange={() => marcarCorreta(i, k)} title="Resposta correta" />
                    <input className="doc-input" placeholder={`Alternativa ${k + 1}`} value={o.texto} onChange={e => setOpcaoTexto(i, k, e.target.value)} />
                    {q.opcoes.length > 2 && (
                      <button type="button" className="quiz-edit-rm" onClick={() => removeOpcao(i, k)} title="Remover alternativa"><i className="bi bi-x-lg"></i></button>
                    )}
                  </div>
                ))}
                <button type="button" className="quiz-edit-add" onClick={() => addOpcao(i)}><i className="bi bi-plus"></i> Alternativa</button>
              </div>
              <input className="doc-input" style={{ marginTop: 8 }} placeholder="Explicação (opcional, aparece na correção)" value={q.explicacao}
                     onChange={e => mutQ(i, x => ({ ...x, explicacao: e.target.value }))} />
            </div>
          ))}
          <button type="button" className="quiz-edit-add-q" onClick={addQuestao}><i className="bi bi-plus-lg"></i> Adicionar pergunta</button>

          <div className="doc-form-acoes">
            <button type="submit" className="btn-centrao" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar quiz'}</button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Cancelar</button>
          </div>
        </form>
      </main>
    </>
  );
}
